import { Chip, Link } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { ExternalLink, ShieldAlert } from "lucide-react";
import type { GetPaymentDispute } from "@/server-types";
import { cn } from "@/utils";
import {
  getDisputeReasonLabel,
  getDisputeStatusColor,
  getDisputeStatusLabel,
  isDisputeActionNeeded,
} from "@/lib/paymentDispute";

type PaymentDispute = NonNullable<GetPaymentDispute["response"]["dispute"]>;

export const PaymentDisputeCard = ({
  dispute,
}: {
  dispute: PaymentDispute | null | undefined;
}) => {
  if (!dispute) {
    return null;
  }

  const actionNeeded = isDisputeActionNeeded(dispute.status);

  return (
    <div
      className={cn("flex flex-col gap-2 rounded-lg border-1 p-3", {
        "border-danger-200 bg-danger-50": actionNeeded,
        "border-warning-200 bg-warning-50": !actionNeeded,
      })}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={cn("flex items-center gap-2 text-sm font-semibold", {
            "text-danger-800": actionNeeded,
            "text-warning-800": !actionNeeded,
          })}
        >
          <ShieldAlert size={16} />
          <span>
            <Trans>Spor (chargeback)</Trans>
          </span>
        </div>
        <Chip
          size="sm"
          variant="flat"
          color={getDisputeStatusColor(dispute.status)}
        >
          {getDisputeStatusLabel(dispute.status)}
        </Chip>
      </div>

      <div className="flex flex-col gap-1">
        <DisputeRow label={t`Razlog`}>
          {getDisputeReasonLabel(dispute.reason)}
        </DisputeRow>
        <DisputeRow label={t`Znesek spora`}>
          {dispute.amountFormatted}
        </DisputeRow>
        <DisputeRow label={t`Odprto`}>{dispute.openedAt}</DisputeRow>
        {dispute.evidenceDueBy && (
          <DisputeRow label={t`Rok za odgovor`}>
            <span
              className={cn({
                "text-danger-700 font-medium": dispute.isEvidencePastDue,
              })}
            >
              {dispute.evidenceDueBy}
              {dispute.isEvidencePastDue ? ` (${t`potekel`})` : ""}
            </span>
          </DisputeRow>
        )}
        <DisputeRow label={t`Dokazila`}>
          {dispute.hasEvidence ? t`Oddana` : t`Niso oddana`}
        </DisputeRow>
      </div>

      <Link
        href={dispute.dashboardUrl}
        isExternal
        showAnchorIcon
        anchorIcon={<ExternalLink size={14} className="ml-1" />}
        color="secondary"
        className="text-xs"
      >
        <Trans>Upravljaj spor v Stripe</Trans>
      </Link>
    </div>
  );
};

const DisputeRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex justify-between gap-4 text-xs">
    <span className="text-foreground-500 shrink-0">{label}</span>
    <span className="text-right">{children}</span>
  </div>
);
